import SEO, { breadcrumbs } from '../components/common/SEO'
import PageHero from '../components/common/PageHero'
import ResponsiveImage from '../components/common/ResponsiveImage'
import {
  Breadcrumbs,
  BookButton,
  ProseSections,
  BulletList,
  PricingList,
  SideCard,
  PractitionerGrid,
  FaqSection,
  LinkCards,
  BookingBand,
} from '../components/detail/DetailBlocks'
import { serviceBySlug } from '../data/servicePages'
import { conditionBySlug } from '../data/conditions'
import { SITE_URL } from '../data/site'
import { faqSchema } from '../data/faqs'
import NotFound from './NotFound'

const priceValue = (price) => price.replace(/[^0-9.]/g, '')

const ServiceDetailPage = ({ slug }) => {
  const service = serviceBySlug(slug)
  if (!service) return <NotFound />

  const path = `/services/${service.slug}`
  const trail = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: service.name, path },
  ]

  const relatedConditions = service.relatedConditions.map(conditionBySlug).filter(Boolean)
  const relatedServices = service.relatedServices.map(serviceBySlug).filter(Boolean)

  return (
    <>
      <SEO
        title={service.title}
        description={service.description}
        path={path}
        image={service.image}
        imageAlt={service.imageAlt}
        noIndex={!service.reviewed}
        structuredData={[
          breadcrumbs(trail),
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            '@id': `${SITE_URL}${path}#service`,
            name: service.name,
            serviceType: service.name,
            description: service.intro,
            url: `${SITE_URL}${path}`,
            image: `${SITE_URL}${service.image}`,
            provider: { '@id': `${SITE_URL}/#business` },
            areaServed: { '@type': 'City', name: 'Toronto' },
            offers: service.pricing
              .filter((p) => /\$\s*\d/.test(p.price))
              .map((p) => ({
                '@type': 'Offer',
                name: `${service.name} — ${p.duration}`,
                price: priceValue(p.price),
                priceCurrency: 'CAD',
                url: `${SITE_URL}${path}`,
              })),
          },
          faqSchema(service.faqs.map((f) => ({ q: f.q, a: f.a }))),
        ]}
      />

      <div className="bg-cream-200">
        <PageHero title={service.h1} subtitle={service.eyebrow} texture="earth-grain" size="sm">
          <p className="font-sans text-dark-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            {service.intro}
          </p>
          <BookButton label={service.bookLabel} />
        </PageHero>
      </div>

      <div className="bg-cream-200">
        <Breadcrumbs items={trail} />
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid lg:grid-cols-[1fr_380px] gap-10 lg:gap-16 items-start">
            <div>
              <div className="rounded-[20px] overflow-hidden shadow-lg mb-10 lg:hidden">
                <ResponsiveImage
                  src={service.image}
                  alt={service.imageAlt}
                  sizes="100vw"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <ProseSections sections={service.sections} />
            </div>

            <aside className="space-y-6 lg:sticky lg:top-28">
              <div className="rounded-[20px] overflow-hidden shadow-lg hidden lg:block">
                <ResponsiveImage
                  src={service.image}
                  alt={service.imageAlt}
                  sizes="380px"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <SideCard>
                <h2 className="font-display text-dark-700 text-xl font-semibold mb-4">
                  {service.benefitsTitle}
                </h2>
                <BulletList items={service.benefits} />
              </SideCard>
              <SideCard>
                <h2 className="font-display text-dark-700 text-xl font-semibold mb-2">Pricing</h2>
                <PricingList pricing={service.pricing} />
                {service.pricingNote && (
                  <p className="font-sans text-dark-400 text-sm leading-relaxed mt-4">
                    {service.pricingNote}
                  </p>
                )}
                <BookButton label={service.bookLabel} className="mt-6 w-full text-center" />
              </SideCard>
            </aside>
          </div>
        </section>
      </div>

      <PractitionerGrid slugs={service.practitioners} />
      <FaqSection faqs={service.faqs} heading={`${service.name} FAQs`} />
      <LinkCards
        label="Conditions"
        heading={`Conditions ${service.name.toLowerCase()} can help with`}
        items={relatedConditions.map((c) => ({
          to: `/conditions/${c.slug}`,
          name: c.name,
          description: c.intro,
          image: c.image,
          imageAlt: c.imageAlt,
        }))}
      />
      <LinkCards
        bg="bg-cream-200"
        label="Related services"
        heading="You might also consider"
        items={relatedServices.map((s) => ({
          to: `/services/${s.slug}`,
          name: s.name,
          description: s.intro,
        }))}
      />
      <BookingBand label={service.bookLabel} />
    </>
  )
}

export default ServiceDetailPage
